import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, X as XIcon } from "lucide-react";

// Flyer selection shared by the builder and concern pages, persisted per dealer
// so a customer can pick products across protocols before creating a brochure.
const key = (dealer) => `postcue.flyer.${dealer || "orevital"}`;

export function useFlyerSelection(dealer) {
  const [ids, setIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(key(dealer)) || "[]");
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key(dealer), JSON.stringify(ids));
    } catch {
      /* ignore */
    }
  }, [ids, dealer]);
  const toggle = useCallback((id) => setIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])), []);
  const clear = useCallback(() => setIds([]), []);
  return { ids, toggle, clear, has: (id) => ids.includes(id) };
}

// Sticky bar: "N products in your flyer → Create flyer".
export function FlyerBar({ dealer, ids, onClear, focus = "" }) {
  const navigate = useNavigate();
  if (!ids.length) return null;
  const go = () => navigate(`/protocols/flyer?d=${dealer}&ids=${ids.join(",")}${focus ? `&focus=${encodeURIComponent(focus)}` : ""}`);
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-md rounded-2xl bg-[#0b2545] text-white shadow-2xl px-4 py-3 flex items-center gap-3 print:hidden" data-testid="flyer-bar">
      <FileText size={18} className="text-[#d6ad52] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold leading-tight" data-testid="flyer-bar-count">{ids.length} product{ids.length === 1 ? "" : "s"} in your flyer</div>
        <div className="text-[11px] text-white/70">Create a printable protocol brochure</div>
      </div>
      <button type="button" onClick={onClear} className="text-white/60 hover:text-white p-1" aria-label="Clear flyer" data-testid="flyer-bar-clear"><XIcon size={16} /></button>
      <button type="button" onClick={go} className="h-9 px-4 rounded-full bg-[#d6ad52] text-[#0b2545] text-xs font-bold hover:bg-[#e2bd66] transition-colors" data-testid="flyer-bar-create">Create flyer</button>
    </div>
  );
}
